import React from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '../constants/theme';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * POR QUÉ TE VAS SIN EL PASAJERO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Decisión del fundador (3-oct-2026): pasados 10 minutos desde que el chofer
 * llega, si el pasajero no aparece puede cancelar «sin penalidad y sin
 * problema, pero siempre indicando el motivo: zona peligrosa, zona muy oscura,
 * o el pasajero no se presentó».
 *
 * ── POR QUÉ UN MODAL Y NO UN `Alert.alert` ──────────────────────────────
 *
 * Son tres motivos más «Cancelar» = cuatro botones. **Android enseña como
 * mucho TRES y se traga los demás en silencio**: uno de los motivos
 * desaparecería según el teléfono, sin un error, sin un aviso. Y en la web
 * `Alert.alert` de react-native-web no pinta absolutamente nada.
 *
 * ── LOS CÓDIGOS SON EL CONTRATO CON EL SERVIDOR ─────────────────────────
 *
 * Lo que se guarda es el CÓDIGO, no el texto que lee el chofer. El texto puede
 * cambiar mañana; el código es lo que `MOTIVOS_DE_ESPERA`
 * (functions/src/penalties.ts) valida y lo que queda en el registro de
 * `cancelaciones_sin_falta` que el fundador va a mirar para saber en qué zonas
 * y a qué horas se le caen los viajes. Si el texto y el código fueran la misma
 * cosa, arreglar una tilde partiría el historial en dos.
 */
export const MOTIVOS_DE_ESPERA = [
  {
    codigo: 'PASAJERO_NO_APARECIO',
    titulo: 'El pasajero no se presentó',
    detalle: 'Esperé el tiempo y no bajó nadie.',
    icono: 'person-remove-outline' as const,
  },
  {
    codigo: 'ZONA_PELIGROSA',
    titulo: 'Zona peligrosa',
    detalle: 'No me siento seguro esperando aquí.',
    icono: 'warning-outline' as const,
  },
  {
    codigo: 'ZONA_MUY_OSCURA',
    titulo: 'Zona muy oscura',
    detalle: 'No hay luz y no se ve nada alrededor.',
    icono: 'moon-outline' as const,
  },
];

interface Props {
  visible: boolean;
  minutosEsperando: number;
  onElegir: (codigo: string) => void;
  onCerrar: () => void;
}

export default function MotivoDeEsperaModal({ visible, minutosEsperando, onElegir, onCerrar }: Props) {
  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onCerrar}>
      <View style={styles.fondo}>
        <View style={styles.caja}>
          <View style={styles.asa} />
          <Text style={styles.titulo}>Cancelar sin que te cuente</Text>
          <Text style={styles.bajada}>
            Llevas {minutosEsperando} minutos esperando. Esta cancelación no te cuenta como falta ni
            te cobra multa. Dinos qué pasó.
          </Text>

          <ScrollView style={{ width: '100%' }} keyboardShouldPersistTaps="handled">
            {MOTIVOS_DE_ESPERA.map((m) => (
              <TouchableOpacity key={m.codigo} style={styles.opcion} onPress={() => onElegir(m.codigo)}>
                <View style={styles.opcionIcono}>
                  <Ionicons name={m.icono} size={22} color={Colors.primary} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.opcionTitulo}>{m.titulo}</Text>
                  <Text style={styles.opcionDetalle}>{m.detalle}</Text>
                </View>
                <Ionicons name="chevron-forward" size={20} color={Colors.textSecondary} />
              </TouchableOpacity>
            ))}
          </ScrollView>

          <TouchableOpacity style={styles.volver} onPress={onCerrar}>
            <Text style={styles.volverTexto}>Mejor sigo esperando</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  fondo: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'flex-end' },
  caja: {
    backgroundColor: Colors.card,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 22,
    paddingBottom: 34,
    maxHeight: '80%',
    alignItems: 'center',
  },
  asa: {
    width: 42,
    height: 5,
    borderRadius: 3,
    backgroundColor: Colors.border,
    marginBottom: 16,
  },
  titulo: { fontSize: 20, fontWeight: '800', color: Colors.text, marginBottom: 6 },
  bajada: {
    fontSize: 14,
    color: Colors.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 18,
  },
  opcion: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.035)',
    borderRadius: 14,
    padding: 14,
    marginBottom: 10,
  },
  opcionIcono: {
    width: 42,
    height: 42,
    borderRadius: 12,
    backgroundColor: 'rgba(0,200,83,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  opcionTitulo: { fontSize: 15, fontWeight: '700', color: Colors.text },
  opcionDetalle: { fontSize: 12, color: Colors.textSecondary, marginTop: 2 },
  volver: { paddingVertical: 14, marginTop: 6 },
  volverTexto: { color: Colors.textSecondary, fontWeight: '600', fontSize: 15 },
});
